import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, type OnDestroy, type OnInit } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { FrequentlyAskedQuestionsTab } from '../../models/faq.model';
import type { Tab } from '../../models/tab.model';
import { SafePipe } from '../../pipes/safe.pipe';
import { ConfigResourceService } from '../../services/config-resource.service';
import { ContentHeadingTabsComponent } from '../../shared/content-heading-tabs/content-heading-tabs.component';
import { ResourceTemplateComponent } from '../../shared/resource-template/resource-template.component';

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatDialogModule, SafePipe, ResourceTemplateComponent, ContentHeadingTabsComponent]
})
export class FaqComponent implements OnInit, OnDestroy {
  @Input() footerStickyEnabled: boolean = true;

  activeTab: FrequentlyAskedQuestionsTab = FrequentlyAskedQuestionsTab.COMMON;
  readonly FrequentlyAskedQuestionsTab = FrequentlyAskedQuestionsTab;
  headerText: string = '';
  commonQuestionsContent: string = '';
  patientQuestionsContent: string = '';
  footerContent: string = '';
  commonQuestionsTabText: string = '';
  patientQuestionsTabText: string = '';
  tabs: Array<Tab<FrequentlyAskedQuestionsTab>> = [];
  private readonly subscriptions = new Subscription();

  constructor(private readonly configService: ConfigResourceService, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.configService.configLoaded$.subscribe({
        next: loaded => {
          if (loaded) {
            this.headerText = this.configService.getProcessedResourceValue<string>('Spa_Site_FAQ', 'Frequently Asked Questions');
            this.commonQuestionsContent = this.configService.getProcessedResourceValue<string>('Spa_Site_FAQ_Content_CommonQuestions', '');
            this.patientQuestionsContent = this.configService.getProcessedResourceValue<string>(
              'Spa_Site_FAQ_Content_PatientQuestions',
              ''
            );
            this.footerContent = this.configService.getProcessedResourceValue<string>('Spa_Site_FAQ_Content_Footer', '');
            this.commonQuestionsTabText = this.configService.getProcessedResourceValue<string>(
              'Spa_Site_FAQ_Tab_CommonQuestions',
              'Most Common Questions'
            );
            this.patientQuestionsTabText = this.configService.getProcessedResourceValue<string>(
              'Spa_Site_FAQ_Tab_PatientQuestions',
              'Patient Questions'
            );
            const defaultTabs: Array<Tab<FrequentlyAskedQuestionsTab>> = this.configService.getProcessedResourceValue<
              Array<Tab<FrequentlyAskedQuestionsTab>>
            >('Spa_Site_FAQ_Tab_Items', [
              { id: FrequentlyAskedQuestionsTab.COMMON, label: 'Most Common Questions', active: true },
              { id: FrequentlyAskedQuestionsTab.PATIENT, label: 'Patient Questions', active: false }
            ]);
            this.setTabs(defaultTabs, this.activeTab);
            this.cdr.markForCheck();
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setActiveTab(tab: Tab<FrequentlyAskedQuestionsTab>): void {
    if (tab) {
      this.activeTab = tab.id;
      this.setTabs(this.tabs, this.activeTab);
    }
  }

  private setTabs(defaultTabs: Array<Tab<FrequentlyAskedQuestionsTab>>, activeTab: FrequentlyAskedQuestionsTab): void {
    this.tabs = defaultTabs.map(tab => ({ ...tab, active: tab.id === activeTab }));
  }
}
